import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type PageProps } from '@/types';
import { Head, Link, usePage } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { route } from 'ziggy-js';

interface TemplateListItem {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    category: string | null;
    is_active: boolean;
    sort_order: number;
    fields_count: number;
    service_requests_count: number;
    created_at: string | null;
}

interface TemplateIndexPageProps extends PageProps {
    templates: TemplateListItem[];
    [key: string]: unknown;
}

export default function AdminServiceRequestTemplatesIndexPage() {
    const { props } = usePage<TemplateIndexPageProps>();
    const { templates } = props;
    const { t } = useTranslations();

    const breadcrumbs = [
        { title: t('service_requests.admin_queue_title', 'Service Request Queue'), href: '/admin/service-requests' },
        { title: t('service_requests.template_manager_title', 'Request Templates'), href: '/admin/service-request-templates' },
    ];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('service_requests.template_manager_title', 'Request Templates')} />

            <PageHeader
                title={t('service_requests.template_manager_title', 'Request Templates')}
                subtitle={t('service_requests.template_manager_subtitle', 'Manage the templates users can submit.')}
                actions={
                    <Button asChild>
                        <Link href={route('admin.service-request-templates.create')}>
                            <Plus className="mr-2 h-4 w-4" />
                            {t('service_requests.create_template', 'Create Template')}
                        </Link>
                    </Button>
                }
            />

            <PageContent>
                <div className="bg-card overflow-hidden rounded-xl">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>{t('service_requests.category', 'Category')}</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Fields</TableHead>
                                <TableHead>Submissions</TableHead>
                                <TableHead>Sort</TableHead>
                                <TableHead className="text-right"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {templates.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-muted-foreground py-12 text-center">
                                        {t('service_requests.no_templates', 'No templates found.')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                templates.map((tpl) => (
                                    <TableRow key={tpl.id}>
                                        <TableCell>
                                            <div>
                                                <p className="font-semibold">{tpl.name}</p>
                                                <p className="text-muted-foreground text-xs">{tpl.slug}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell>{tpl.category ?? <span className="text-muted-foreground">—</span>}</TableCell>
                                        <TableCell>
                                            <Badge
                                                variant="outline"
                                                className={
                                                    tpl.is_active
                                                        ? 'border-border bg-success-subtle text-success-subtle-foreground'
                                                        : 'border-border bg-muted text-muted-foreground'
                                                }
                                            >
                                                {tpl.is_active
                                                    ? t('service_requests.template_active', 'Active')
                                                    : t('service_requests.template_inactive', 'Inactive')}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{tpl.fields_count}</TableCell>
                                        <TableCell>{tpl.service_requests_count}</TableCell>
                                        <TableCell>{tpl.sort_order}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button asChild variant="outline" size="sm">
                                                    <Link href={route('admin.service-request-templates.edit', tpl.id)}>Edit</Link>
                                                </Button>
                                                <Button asChild variant="ghost" size="sm">
                                                    <Link href={route('admin.service-request-templates.show', tpl.id)}>View</Link>
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </PageContent>
        </AppLayout>
    );
}
